import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getMentoringContent } from '../features/mentoring/mentoringContent';
import { getMentoringPath } from '../features/mentoring/mentoringRoutes';
import MentoringReels from '../features/mentoring/components/MentoringReels';
import './mentoringHome.css';

/**
 * Home-page band for the mentoring section.
 *
 * Reads the section's own content bundle in the visitor's language rather than
 * duplicating copy in the host locale files, so the homepage teaser can never
 * drift from the page it links to.
 */
const MentoringHighlight = () => {
  const { i18n } = useTranslation();
  const locale = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const content = getMentoringContent(locale);
  const href = getMentoringPath(locale, 'index');

  if (!content || !href) {
    return null;
  }

  const { index, nav, shared } = content;
  const steps = index.pipeline.steps.slice(0, 4);
  const ways = index.waysIn.cards;

  return (
    <section
      id="mentoring"
      aria-labelledby="mentoring-highlight-title"
      className="mt-home"
    >
      <div className="mt-home__inner">
        <div className="mt-home__top">
          <p className="mt-eyebrow">{index.hero.eyebrow}</p>
          <h2 id="mentoring-highlight-title" className="mt-title mt-home__title">
            {index.journey.title}
          </h2>
          <p className="mt-lead">{index.journey.lead}</p>
          <div className="mt-home__actions">
            <Link to={href} className="mt-btn mt-btn--on-dark">
              {nav.openCta}
              <span aria-hidden="true">→</span>
            </Link>
            <Link to={getMentoringPath(locale, 'program')} className="mt-btn mt-btn--ghost-dark">
              {index.hero.secondary.label}
            </Link>
          </div>
        </div>

        <dl className="mt-home__facts">
          {index.hero.facts.map((fact) => (
            <div className="mt-home__fact" key={fact.label}>
              <dt>{fact.label}</dt>
              <dd>{fact.value}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="mt-home__band">
        <ol className="mt-home__steps">
          {steps.map((step) => (
            <li className="mt-home__step" key={step.number}>
              <span className="mt-home__step-num">{step.number}</span>
              <span className="mt-home__step-title">{step.title}</span>
              <span className="mt-home__step-text">{step.text}</span>
            </li>
          ))}
        </ol>
      </div>

      <div className="mt-home__inner">
        <div className="mt-home__projects">
          <h3>{index.proof.title}</h3>
          <div className="mt-home__project-grid">
            {index.proof.projects.map((project) => (
              <a key={project.key} href={project.href} target="_blank" rel="noopener noreferrer" className="mt-home__project">
                <span className="mt-home__way-tag">{project.tag}</span>
                <strong>{project.title}</strong>
                <span>{project.text}</span>
                <span className="mt-home__project-link">{locale === 'fr' ? 'Voir le produit' : 'Visit product'} ↗</span>
              </a>
            ))}
          </div>
        </div>
        <ul className="mt-home__ways">
          {ways.map((way) => (
            <li className="mt-home__way" key={way.key}>
              <span className="mt-home__way-tag">{way.tag}</span>
              <span className="mt-home__way-title">{way.title}</span>
              <span className="mt-home__way-text">{way.text}</span>
            </li>
          ))}
        </ul>
        <p className="mt-home__note">{shared.cta.text}</p>
      </div>

      <MentoringReels locale={locale} surface="home" />
    </section>
  );
};

export default MentoringHighlight;
